import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { pdf } from "@react-pdf/renderer";
import ReceiptPDF from "./PDF/ReceiptPDF";
import "./receipt.css";
import "./invoice.css";
import Paid from "../components/Images/paid.png";
import {
  Save,
  RotateCcw,
  FileDown,
  PlusCircle,
  Trash2,
  Building2,
  User2,
  CalendarDays,
  ChevronLeft,
  Mail,
  Phone,
  MapPin,
  BadgeCheck,
} from "lucide-react";

function generateReceiptNumber() {
  return `REC-${Math.floor(Math.random() * 1000000)}`;
}

const CreateReceipt = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { companyId, company } = useCompany();
  const isEdit = Boolean(id);

  const [userId, setUserId] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const [customers, setCustomers] = useState([]);

  const [receiptData, setReceiptData] = useState({
    customerName: "",
    customerAddress: "",
    receiptNumber: generateReceiptNumber(),
    date: new Date().toISOString().split("T")[0],
    items: [{ description: "", quantity: 0, rate: 0, amount: 0 }],
    total: 0,
    notes: "",
  });

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();
      if (userErr || !user) {
        console.error("Auth error:", userErr);
        return;
      }
      setUserId(user.id);

      // company owner
      let ownerId = company?.owner_id;
      if (!ownerId) {
        const { data: comp } = await supabase
          .from("companies")
          .select("owner_id")
          .eq("id", companyId)
          .single();
        ownerId = comp?.owner_id;
      }
      if (ownerId) {
        const { data: ci } = await supabase
          .from("company_info")
          .select(
            "company_name,email,address,phone_number,TIN,logo_url,brand_colors,bank_name,account_name,account_number,payment_instructions"
          )
          .eq("company_id", companyId)
          .single();
        if (ci) setCompanyInfo(ci);
      }

      // customers
      const { data: custs } = await supabase
        .from("customers")
        .select("id, company_name, address")
        .eq("company_id", companyId);
      if (custs) setCustomers(custs);

      // if editing
      if (isEdit) {
        const { data: rec, error: recErr } = await supabase
          .from("receipts")
          .select("*, items")
          .eq("id", id)
          .eq("company_id", companyId)
          .maybeSingle();
        if (recErr) {
          console.error("Error loading receipt:", recErr);
          return;
        }
        if (rec) {
          setReceiptData({
            customerName: rec.customerName,
            customerAddress: rec.customerAddress || rec.customerAddr || "",
            receiptNumber: rec.receiptNumber,
            date: rec.date,
            items: rec.items || [],
            total: rec.total,
            notes: rec.notes || "",
          });
        }
      }
    })();
  }, [companyId, company, id, isEdit]);

  // dynamic brand color (unchanged behavior)
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-color",
      companyInfo.brand_colors
    );
  }, [companyInfo.brand_colors]);

  // Handlers (UNCHANGED logic)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReceiptData((r) => ({ ...r, [name]: value }));
  };

  const addItem = () => {
    setReceiptData((r) => ({
      ...r,
      items: [...r.items, { description: "", quantity: 0, rate: 0, amount: 0 }],
    }));
  };

  const handleItemChange = (idx, field, val) => {
    setReceiptData((r) => {
      const items = [...r.items];
      items[idx][field] = field === "description" ? val : Number(val) || 0;
      items[idx].amount =
        (Number(items[idx].quantity) || 0) * (Number(items[idx].rate) || 0);
      const total = items.reduce((sum, it) => sum + (it.amount || 0), 0);
      return { ...r, items, total };
    });
  };

  const removeItem = (idx) => {
    setReceiptData((r) => {
      const items = r.items.filter((_, i) => i !== idx);
      const total = items.reduce((sum, it) => sum + (it.amount || 0), 0);
      return { ...r, items, total };
    });
  };

  const handleCustomerSelect = (e) => {
    const cid = e.target.value;
    const cust = customers.find((c) => c.id === cid);
    if (cust) {
      setReceiptData((r) => ({
        ...r,
        customerName: cust.company_name,
        customerAddress: cust.address,
      }));
    }
  };

  const saveReceipt = useCallback(async () => {
    if (!userId || !companyId) return;
    const payload = {
      user_id: userId,
      created_by: userId,
      company_id: companyId,
      receiptNumber: receiptData.receiptNumber,
      customerName: receiptData.customerName,
      customerAddress: receiptData.customerAddress,
      date: receiptData.date,
      items: receiptData.items,
      total: receiptData.total,
      notes: receiptData.notes,
    };
    if (isEdit) {
      const { error } = await supabase
        .from("receipts")
        .update(payload)
        .eq("id", id)
        .eq("company_id", companyId);
      if (error) console.error(error);
      else alert("Receipt updated successfully!");
    } else {
      const { error } = await supabase.from("receipts").insert([payload]);
      if (error) console.error(error);
      else alert("Receipt saved successfully!");
    }
    navigate("/totalReceipts");
  }, [userId, companyId, receiptData, id, isEdit, navigate]);

  const printInvoice = async () => {
    try {
      const accent =
        getComputedStyle(document.documentElement)
          .getPropertyValue("--primary-color")
          .trim() || "#008080";

      // ✅ Force status to PAID
      const pdfReceiptData = {
        ...receiptData,
        status: "paid",
      };

      const paymentInstructions = `Payment received successfully. Receipt #: ${pdfReceiptData.receiptNumber}.`;

      const doc = (
        <ReceiptPDF
          companyInfo={companyInfo}
          receiptData={pdfReceiptData}
          accent={accent}
          paymentInstructions={paymentInstructions}
        />
      );

      const blob = await pdf(doc).toBlob();

      const safeNo = String(pdfReceiptData.receiptNumber || "receipt")
        .replace(/[^\w-]+/g, "_")
        .slice(0, 80);

      // ✅ Always PAID filename
      const fileName = `${safeNo}_PAID.pdf`;

      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Could not generate PDF. Please try again.");
    }
  };

  const resetForm = () =>
    setReceiptData({
      customerName: "",
      customerAddress: "",
      receiptNumber: generateReceiptNumber(),
      date: new Date().toISOString().split("T")[0],
      items: [{ description: "", quantity: 0, rate: 0, amount: 0 }],
      total: 0,
      notes: "",
    });

  if (!companyId) return <p>Loading company info…</p>;

  return (
    <div className="receipt-shell">
      {/* LEFT: Editor */}
      <section className="receipt-input panel">
        <header className="rec-head">
          <div className="rec-title">
            <h2>{isEdit ? "Edit Receipt" : "Create Receipt"}</h2>
            <p className="muted">
              <Building2 size={14} /> {companyInfo.company_name || "—"}{" "}
              &nbsp;•&nbsp;
              <User2 size={14} /> {receiptData.customerName || "—"}{" "}
              &nbsp;•&nbsp;
              <CalendarDays size={14} /> {receiptData.date || "—"}
            </p>
          </div>
          <div className="rec-actions">
            <button className="btn-ghost" onClick={() => navigate(-1)}>
              <ChevronLeft size={16} /> Back
            </button>
            <button className="btn-primary" onClick={saveReceipt}>
              <Save size={16} /> {isEdit ? "Update" : "Save"}
            </button>
            <button className="icon-btn" onClick={resetForm} title="Reset">
              <RotateCcw size={16} />
            </button>
            <button
              className="icon-btn"
              onClick={printInvoice}
              title="Download PDF"
            >
              <FileDown size={16} />
            </button>
          </div>
        </header>

        {/* Header form */}
        <div className="rec-card">
          <div className="grid-3">
            <label className="stack">
              Receipt #
              <input
                type="text"
                name="receiptNumber"
                value={receiptData.receiptNumber}
                onChange={handleInputChange}
              />
            </label>

            <label className="stack">
              Customer
              <select
                name="customer"
                value={
                  customers.find(
                    (c) => c.company_name === receiptData.customerName
                  )?.id || ""
                }
                onChange={handleCustomerSelect}
              >
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="stack">
              Date
              <input
                type="date"
                name="date"
                value={receiptData.date}
                onChange={handleInputChange}
              />
            </label>

            <label className="stack">
              Customer Address
              <input
                type="text"
                name="customerAddress"
                value={receiptData.customerAddress}
                readOnly
                placeholder="Auto-filled from customer"
              />
            </label>
          </div>
        </div>

        {/* Items editor */}
        <div className="rec-card">
          <div className="card-head">
            <h3>Items</h3>
            <button onClick={addItem} className="btn-ghost">
              <PlusCircle size={16} /> Add Item
            </button>
          </div>

          <div className="table-wrap">
            <table className="receipt-items-edit">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="num">Qty</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                  <th className="center">Action</th>
                </tr>
              </thead>
              <tbody>
                {receiptData.items.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(idx, "description", e.target.value)
                        }
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(idx, "quantity", e.target.value)
                        }
                        id="edit-input"
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e) =>
                          handleItemChange(idx, "rate", e.target.value)
                        }
                        id="edit-input"
                      />
                    </td>
                    <td className="num">
                      ₦{(item.amount || 0).toLocaleString()}
                    </td>
                    <td className="center">
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => removeItem(idx)}
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!receiptData.items.length && (
                  <tr>
                    <td colSpan="5">
                      <div className="empty-state">
                        No items yet. Add your first line.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes + Summary */}
        <div className="rec-grid">
          <div className="rec-card">
            <h3>Notes</h3>
            <textarea
              name="notes"
              placeholder="Payment method, reference, or any details for the client…"
              value={receiptData.notes}
              onChange={handleInputChange}
            />
          </div>

          <div className="rec-card">
            <h3>Summary</h3>
            <ul className="kv">
              <li>
                <span>Total</span>
                <strong>₦{(receiptData.total || 0).toLocaleString()}</strong>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* RIGHT: Live Preview (IDs preserved) */}
      <aside id="receiptPreview" className="receipt-preview panel">
        {companyInfo.logo_url && (
          <img src={companyInfo.logo_url} alt="Logo" className="logo" />
        )}

        <div id="comp-div">
          <p id="comp-phone">
            {" "}
            <Phone size={8} /> <span>{companyInfo.phone_number}</span>
          </p>
          <p id="comp-email">
            {" "}
            <Mail size={8} /> <span>{companyInfo.email}</span>
          </p>
          <p id="comp-addr">
            <MapPin size={8} /> <span>{companyInfo.address}</span>
          </p>
          <p id="comp-tin">
            {" "}
            <BadgeCheck size={8} /> <span> {companyInfo.TIN} </span>
          </p>
        </div>

        <div className="cust-div">
          <h3 id="cust-title">Recipient Information</h3>

          <h4 id="cust-name">
            {" "}
            <span className="cust-name1">Customer : </span>{" "}
            <span className="cust-name2">{receiptData.customerName}</span>
          </h4>
          <h4 id="cust-addr">
            {" "}
            <span className="cust-name1">Address : </span>{" "}
            <span className="cust-name2">{receiptData.customerAddress}</span>
          </h4>
        </div>

        <div className="receipt-details" data-status={receiptData.status}>
          <h4>Receipt # {receiptData.receiptNumber}</h4>
          <p>Payment Date: {receiptData.date}</p>
        </div>

        <div className="table-wraps">
          <table className="receipt-items">
            <thead>
              <tr>
                <th>Description</th>
                <th className="num">Qty</th>
                <th className="num">Rate (NGN)</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {receiptData.items.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.description}</td>
                  <td className="num">{item.quantity}</td>
                  <td className="num">₦{(item.rate || 0).toLocaleString()}</td>
                  <td className="num">
                    ₦{(item.amount || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="total-line">
          <p className="grand">
            <strong>
              Total: ₦{(receiptData.total || 0).toLocaleString() || 0}
            </strong>
          </p>
        </div>

        {receiptData.notes && (
          <p id="inv-notes" className="notes">
            <strong>Notes:</strong>
            <br />
            <div className="notes-body preserve">{receiptData.notes}</div>
          </p>
        )}

        <small id="footer" className="muted-center">
          Thank you for Your Business ...
        </small>
        <img src={Paid} alt="Paid Icon" id="paid-icon" className="paid-stamp" />
      </aside>

      <button onClick={printInvoice} className="pdf-btn btn-primary">
        <FileDown size={16} /> Print / Save as PDF
      </button>
    </div>
  );
};

export default CreateReceipt;
